import moment from 'moment';

/**
 * Read a time as `H:mm` or `HH:mm`, with optional `:ss` (ignored) and surrounding whitespace.
 * Minutes must have two digits: `8:5` could mean 08:05 or 08:50, so it's rejected.
 * Returns [hour, minute], or null if it isn't a valid time of day.
 */
export function parseTime(time: string | null | undefined): [number, number] | null {
  const t = time ? /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time.trim()) : null;
  if (!t) return null;
  const [hour, minute] = [Number(t[1]), Number(t[2])];
  return hour <= 23 && minute <= 59 ? [hour, minute] : null;
}

/** Format a readable time as `HH:mm`, or return null if it can't be read. */
export function formatTime(time: string | null | undefined): string | null {
  const t = parseTime(time);
  return t ? t.map((n) => String(n).padStart(2, '0')).join(':') : null;
}

/** Read a typed date as `D/M/YYYY` (strictly) and return it as `YYYY-MM-DD`, or null. */
export function parseDisplayDate(value: string | null | undefined): string | null {
  const d = value ? /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim()) : null;
  if (!d) return null;
  const [, day = '', month = '', year = ''] = d;
  const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  // Reject dates that don't exist, e.g. 31/02/2026
  return isValidDateValue(combineLocalDateTime(iso, '12:00')) ? iso : null;
}

/** True for a Date that holds a real instant (not null and not an Invalid Date). */
export function isValidDateValue(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

/**
 * Combine a date (`YYYY-MM-DD`) and a time (see parseTime) into a Date at that wall-clock
 * time in the browser's time zone.
 *
 * Returns an invalid Date until both parts are entered, or when either is unreadable,
 * the date doesn't exist (e.g. 2026-02-30), or the time doesn't exist on that day because
 * of a daylight-saving change.
 */
export function combineLocalDateTime(date: string | null, time: string | null): Date {
  const d = date ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(date) : null;
  const t = parseTime(time);
  if (!d || !t) return new Date(NaN);

  const [year, month, day] = [Number(d[1]), Number(d[2]) - 1, Number(d[3])];
  const [hour, minute] = t;
  const result = new Date(year, month, day, hour, minute);
  // The Date constructor rolls over out-of-range days and moves times in a DST gap,
  // instead of failing
  if (
    result.getFullYear() !== year ||
    result.getMonth() !== month ||
    result.getDate() !== day ||
    result.getHours() !== hour ||
    result.getMinutes() !== minute
  ) {
    return new Date(NaN);
  }
  return result;
}

/** Show a date-time as `DD/MM/YYYY HH:mm` (24-hour, browser time zone), or '' if invalid. */
export function formatDisplayDateTime(date: Date): string {
  return isValidDateValue(date) ? moment(date).format('DD/MM/YYYY HH:mm') : '';
}

/**
 * Read `D/M/YYYY H:mm` or `DD/MM/YYYY HH:mm` (an optional comma after the date) with the same
 * strict rules as the date and time inputs. Returns an invalid Date if it can't be read.
 */
export function parseDisplayDateTime(text: string | null | undefined): Date {
  const m = text ? /^(\S+?),?\s+(\S+)$/.exec(text.trim()) : null;
  const date = m ? parseDisplayDate(m[1]) : null;
  // HH:mm only: seconds would be silently dropped
  const time = m?.[2] && /^\d{1,2}:\d{2}$/.test(m[2]) ? m[2] : null;
  return date && time ? combineLocalDateTime(date, time) : new Date(NaN);
}
